import * as React from 'react';
import * as DateFns from 'date-fns';
import validator, { Rule, RulesMessages } from '../utils/validator';
import DateInput from './DateInput';
import Calendar from './Calendar';
import { HatType } from './Hat';

interface DatePickerProps {
  name: string;
  value?: string;
  id: string;
  label: string;
  placeHolder?: string;
  required?: boolean;
  focus?: boolean;
  hasError?: boolean;
  errors?: any;
  theme?: 'night' | 'day';
  validationRules?: Rule[];
  rulesMessages?: RulesMessages;
  onChange?: Function;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  minDate?: Date | string;
  maxDate?: Date | string;
  unAvailableDates?: Date[];
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  priorityDate?: Date;
  language?: 'enUS' | 'es';
  children?: JSX.Element;
  hat?: HatType[];
  readOnly?: boolean;
  disabled?: boolean;
  autocomplete?: string;
}

interface DatePickerState {
  calendarOpen: boolean;
  inputValue: string;
}

export default function DatePicker(props: DatePickerProps) {
  const [state, setState] = React.useState<DatePickerState>({
    calendarOpen: false,
    inputValue: props.value || ''
  });

  React.useEffect(() => {
    if (props.value && state.inputValue !== props.value) {
      setState({ ...state, inputValue: props.value });
    }
  }, [props.value, props.readOnly]);

  return (
    <>
      <DateInput
        name={props.name}
        value={state.inputValue}
        disablePastDates={props.disablePastDates}
        disableFutureDates={props.disableFutureDates}
        rulesMessages={props.rulesMessages}
        maxDate={props.maxDate}
        minDate={props.minDate}
        id={props.id}
        label={props.label}
        placeHolder={props.placeHolder}
        theme={props.theme}
        required={props.required}
        hasError={props.hasError}
        errors={props.errors}
        autocomplete={props.autocomplete}
        onChange={(e: any) => {
          if (props.onChange !== undefined) {
            props.onChange(e);
          }
          setState({ ...state, inputValue: e.target.value });
        }}
        onComplete={props.onComplete}
        onError={props.onError}
        onBlur={props.onBlur}
        hat={props.hat}
        readOnly={props.readOnly}
        disabled={props.disabled}
      >
        {!props.readOnly && (
          <div style={{ position: 'relative' }}>
            <div
              role="button"
              aria-label={`Choose Date${
                state.inputValue ? ` Selected Date is ${state.inputValue}` : ''
              }`}
              className={`${state.calendarOpen ? 'close' : 'calendar'} icon`}
              onMouseDown={(e: any) => {
                e.preventDefault();
                setState({ ...state, calendarOpen: !state.calendarOpen });
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13 || e.keyCode === 32) {
                  setState({ ...state, calendarOpen: !state.calendarOpen });
                }
              }}
              tabIndex={0}
            >
              {' '}
            </div>
          </div>
        )}
      </DateInput>
      {state.calendarOpen && !props.hasError && (
        <>
          <Calendar
            minDate={props.minDate}
            maxDate={props.maxDate}
            unAvailableDates={props.unAvailableDates}
            disablePastDates={props.disablePastDates}
            disableFutureDates={props.disableFutureDates}
            language={props.language}
            priorityDate={props.priorityDate}
            onBlur={() => {
              setState({ ...state, calendarOpen: false });
            }}
            onSelect={(day: Date) => {
              if (props.onChange !== undefined) {
                props.onChange({
                  target: {
                    value: DateFns.format(day, 'MM/dd/yyyy'),
                    valid: true
                  }
                });
              }
              setState({
                ...state,
                calendarOpen: false,
                inputValue: DateFns.format(day, 'MM/dd/yyyy').replace(
                  /\//g,
                  ' / '
                )
              });
              if (props.onComplete !== undefined) {
                props.onComplete(
                  DateFns.format(day, 'MM/dd/yyyy').replace(/ /g, '')
                );
              }
            }}
            selectedDate={
              state.inputValue &&
              validator.isValidDate(
                state.inputValue.replace(/ /g, ''),
                'MM/dd/yyyy'
              )
                ? new Date(state.inputValue)
                : undefined
            }
          >
            {props.children}
          </Calendar>
          <div className="popupParentWindow">&nbsp;</div>
        </>
      )}
    </>
  );
}
